import { StoredNotification } from '../../model/StoredNotification';
import { StoredApplicationMap, StoredStreamMap } from '../../store/State';

import { getSourceTitle, getStreamTitle } from './SourceInfo';
import { getDateTitle } from './Time';

export enum GroupingType {
  APPLICATION = 'Application',
  DATE = 'Date',
  PRIORITY = 'Priority',
}

export const GroupingNamesMap: Record<GroupingType, string> = {
  [GroupingType.APPLICATION]: 'Sender',
  [GroupingType.DATE]: 'Date',
  [GroupingType.PRIORITY]: 'Priority',
};

export const PriorityTitles = ['Uncategorized', 'Low', 'Medium', 'High'];

/**
 * A notification group - a set of notifications that should be grouped together
 * within the UI.
 *
 * Grouping method can vary, but all grouping methods will produce a set of
 * these groups.
 */
export interface Group {
  /**
   * Unique key for this group. Mainly used for storing a date string so as time passes we do not need to recalculate the date to group the notifications by.
   */
  key: string;
  /**
   * The grouping type used to create this group.
   */
  type: GroupingType;

  /**
   * Priority of this group. Only used for GroupingType.PRIORITY. Will be random otherwise.
   */
  priority: number;

  /**
   * User-visibile title that will be displayed above these notifications
   */
  title: string;

  /**
   * The notifications to display within this group.
   *
   * This array should always contain at least one notification. If there are no
   * notifications that fit within this category then the group should be deleted.
   */
  notifications: StoredNotification[];

  /**
   * Group's render info to be used for progressive card rendering
   */
  renderLimit?: number;
}

/**
 * A map for expanded group bookkeeping
 */
export interface GroupExpansionMap {
  [key: string]: boolean;
}

/**
 * When creating a new notification group, this function determines the
 * user-visible title for the group
 */
export function getGroupTitle(
  notification: StoredNotification,
  groupingType: GroupingType
): string {
  switch (groupingType) {
    case GroupingType.APPLICATION:
      return notification.title;
    case GroupingType.DATE:
      return getDateTitle(notification.notification.date);
    case GroupingType.PRIORITY:
      return getPriorityTitle(notification);
    default:
      throw new Error(`invalid groupMethod : ${groupingType}`);
  }
}

/**
 * Returns the appropriate label for the indicator type
 */
export function getPriorityTitle(notification: StoredNotification): string {
  const priority = notification.notification.priority ? notification.notification.priority : 1;
  return PriorityTitles[priority - 1];
}

/**
 * Returns a string by which notifications can be grouped together. The name of the group is determined by
 * {@link getGroupTitle}.
 */
export function getGroupKey(notification: StoredNotification, groupingType: GroupingType): string {
  const { source } = notification;
  const titledGroupingTypes = [GroupingType.DATE, GroupingType.PRIORITY];

  if (titledGroupingTypes.includes(groupingType)) {
    return getGroupTitle(notification, groupingType);
  } else if (source.type === 'desktop') {
    return notification.notification.stream?.id || source.identity.uuid;
  } else {
    return '';
  }
}

/**
 * Group notifications together based on the given grouping method e.g. application/date.
 * @param notifications List of notifications to sort into groups
 * @param applications A map of application registry to resolve the notification title
 * @param streams A map of stored streams to resolve the "notification sender" title
 * @param groupMethod Grouping type to use for sorting
 */
export function groupNotifications(
  notifications: StoredNotification[],
  applications: StoredApplicationMap,
  streams: StoredStreamMap,
  groupMethod: GroupingType
): Group[] {
  const groupMap = notifications
    .map((notification) => {
      // Map stored notifications to titled notifications
      return {
        ...notification,
        title:
          getStreamTitle(streams, notification.notification.stream?.id) ||
          getSourceTitle(notification.source, applications),
      };
    })
    .sort((a: StoredNotification, b: StoredNotification) => {
      // Sort notifications by date (groups will then also be sorted by date)
      return b.notification.date.valueOf() - a.notification.date.valueOf();
    })
    .reduce((groups: Map<string, Group>, notification: StoredNotification) => {
      const key = getGroupKey(notification, groupMethod);

      // If group title already exists just add it to the group
      if (groups.has(key)) {
        const group = groups.get(key);
        group && group.notifications.push(notification);
      } else {
        groups.set(key, {
          key,
          type: groupMethod,
          title: getGroupTitle(notification, groupMethod),
          notifications: [notification],
          priority: notification.notification.priority,
        });
      }
      return groups;
    }, new Map<string, Group>());

  const groupedNotifications = Array.from(groupMap.values());

  if (groupMethod === GroupingType.PRIORITY) {
    groupedNotifications.sort((a: Group, b: Group) => b.priority - a.priority);
  }

  return groupedNotifications;
}
